import { EntityPageContent } from './EntityPageContent';
import type {
  EntityStatusKey,
  EntityTrendsPageViewModel,
} from '@/features/shared/hooks/useEntityTrendsPage';
import type { ReactElement, ReactNode } from 'react';

type EntityTrendsPageContentSectionProps<TEntityItem> = {
  viewModel: EntityTrendsPageViewModel<TEntityItem>;
  titleActions: ReactNode;
  footer?: ReactNode;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  chartEmptyStateTitle: string;
  entityTableNoRowsText: string;
  executionTableNoRowsText: string;
  renderEntityTable: (input: { showDeltas: boolean; noRowsText: string }) => ReactElement;
  renderExecutionsTable: (input: { noRowsText: string }) => ReactElement;
  contentAbove?: ReactNode;
  contentBelow?: ReactNode;
  contentOverride?: ReactNode;
};

export const EntityTrendsPageContentSection = <TEntityItem,>({
  viewModel,
  titleActions,
  footer,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  chartEmptyStateTitle,
  entityTableNoRowsText,
  executionTableNoRowsText,
  renderEntityTable,
  renderExecutionsTable,
  contentAbove,
  contentBelow,
  contentOverride,
}: EntityTrendsPageContentSectionProps<TEntityItem>): ReactElement => {
  return (
    <EntityPageContent
      titleActions={titleActions}
      footer={footer}
      range={viewModel.range}
      chartTab={viewModel.chartTab}
      activeTab={viewModel.activeTab}
      entityTabValue="entity"
      searchValue={viewModel.searchValue}
      pickerStartDay={viewModel.pickerStartDay}
      pickerEndDay={viewModel.pickerEndDay}
      hasCustomDates={viewModel.hasCustomDates}
      isLatestMode={viewModel.isLatestMode}
      metricCards={viewModel.metricCards}
      selectedStatuses={viewModel.selectedStatuses}
      statusOptions={viewModel.statusFilterOptions}
      selectedTags={viewModel.selectedTags}
      tagOptions={viewModel.tagOptions}
      tagOptionsLoading={viewModel.tagOptionsLoading}
      selectedProjects={viewModel.selectedProjects}
      onProjectsChange={viewModel.handleProjectsChange}
      projectOptions={viewModel.showProjectFilter ? viewModel.projectFilterOptions : undefined}
      metricValueItems={viewModel.metricValueItems}
      chartHeight={viewModel.chartHeight}
      showMetricCardsSkeleton={viewModel.showPageSkeleton || viewModel.showMetricCardsSkeleton}
      showChartSkeleton={viewModel.showPageSkeleton || viewModel.showChartSkeleton}
      showChartErrorState={viewModel.showChartErrorState}
      suppressChartAnimation={viewModel.suppressChartAnimation}
      showLineChartPoints={viewModel.showLineChartPoints}
      filteredStackedChartData={viewModel.filteredStackedChartData}
      filteredPassRateChartData={viewModel.filteredPassRateChartData}
      filteredFrequencyChartData={viewModel.filteredFrequencyChartData}
      filteredDurationChartData={viewModel.filteredDurationChartData}
      deltasEnabled={viewModel.deltasEnabled}
      onRangeChange={viewModel.handleRangeChange}
      onSearchChange={viewModel.handleSearchChange}
      onChartTabChange={viewModel.handleChartTabChange}
      onActiveTabChange={viewModel.handleActiveTabChange}
      onStatusToggle={(status) => {
        viewModel.handleStatusToggle(status as EntityStatusKey);
      }}
      onStatusesChange={(nextStatuses) => {
        viewModel.handleStatusesChange(nextStatuses as EntityStatusKey[]);
      }}
      onTagsChange={viewModel.handleTagsChange}
      onChartSelect={viewModel.handleChartSelect}
      onSelectedStartDayChange={viewModel.handleSelectedStartDayChange}
      onSelectedEndDayChange={viewModel.handleSelectedEndDayChange}
      showChartDateClear={viewModel.showChartDateClear}
      onChartDateClear={viewModel.handleChartDateClear}
      title={title}
      addButtonLabel={addButtonLabel}
      searchAriaLabel={searchAriaLabel}
      chartTabsAriaLabel={chartTabsAriaLabel}
      pageTabsAriaLabel={pageTabsAriaLabel}
      entityTabLabel={entityTabLabel}
      chartEmptyStateTitle={chartEmptyStateTitle}
      entityTableNoRowsText={entityTableNoRowsText}
      executionTableNoRowsText={executionTableNoRowsText}
      contentAbove={contentAbove}
      contentBelow={contentBelow}
      contentOverride={contentOverride}
      renderEntityTable={(showTableDeltas) =>
        renderEntityTable({ showDeltas: showTableDeltas, noRowsText: entityTableNoRowsText })
      }
      renderExecutionsTable={() => renderExecutionsTable({ noRowsText: executionTableNoRowsText })}
    />
  );
};
